import { useEffect, useState } from 'react';
import { ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';

interface RejectRefinementModalProps {
    open: boolean;
    patientName: string;
    isSubmitting: boolean;
    onSubmit: (reason: string) => void;
    onClose: () => void;
}

export function RejectRefinementModal({
    open,
    patientName,
    isSubmitting,
    onSubmit,
    onClose
}: RejectRefinementModalProps) {
    const [reason, setReason] = useState('');
    const [acknowledged, setAcknowledged] = useState(false);

    useEffect(() => {
        if (open) {
            setReason('');
            setAcknowledged(false);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent): void => {
            if (e.key === 'Escape' && !isSubmitting) onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, isSubmitting, onClose]);

    if (!open) return null;

    const canSubmit = reason.trim().length > 0 && acknowledged && !isSubmitting;

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="reject-refinement-title"
            className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center"
        >
            <div className="w-full max-w-[500px] bg-white rounded-2xl shadow-xl overflow-hidden">
                {/* Header */}
                <div className="px-5 py-3.5 border-b border-gray-200 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-yellow-100">
                        <ArrowPathIcon className="w-4 h-4 text-yellow-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div
                            id="reject-refinement-title"
                            className="text-sm font-bold text-gray-900"
                        >
                            Reject refinement
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 truncate">
                            {patientName || 'Patient'}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        aria-label="Close"
                        className="w-7 h-7 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors disabled:opacity-50"
                    >
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-5 py-4 flex flex-col gap-3">
                    <div>
                        <label
                            htmlFor="reject-reason"
                            className="block text-xs font-semibold text-gray-700 mb-1.5"
                        >
                            Reason <span className="font-normal text-gray-400">(required)</span>
                        </label>
                        <textarea
                            id="reject-reason"
                            rows={4}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Explain why you are rejecting the refinement"
                            disabled={isSubmitting}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500 resize-none disabled:opacity-50"
                            autoFocus
                        />
                    </div>

                    <div className="bg-yellow-50 rounded-lg p-4">
                        <button
                            type="button"
                            onClick={() => setAcknowledged((v) => !v)}
                            className="flex items-start gap-2.5 cursor-pointer text-left w-full"
                        >
                            <span
                                className={`w-4 h-4 rounded flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors ${
                                    acknowledged
                                        ? 'bg-yellow-400 border-yellow-500'
                                        : 'bg-white border-gray-400'
                                }`}
                                style={{ borderWidth: '1.5px', borderStyle: 'solid' }}
                            >
                                {acknowledged && (
                                    <CheckIcon
                                        className="w-2.5 h-2.5 text-gray-900"
                                        strokeWidth={3}
                                    />
                                )}
                            </span>
                            <span className="text-xs text-gray-700 font-medium leading-snug">
                                ODO recommends proceeding with refinement. I acknowledge I am
                                choosing to reject it.
                            </span>
                        </button>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="px-4 py-2 rounded-md border border-gray-300 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => canSubmit && onSubmit(reason.trim())}
                        disabled={!canSubmit}
                        className="px-4 py-2 rounded-md text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 bg-yellow-400 text-gray-900 hover:bg-yellow-500"
                    >
                        {isSubmitting && <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />}
                        Reject refinement
                    </button>
                </div>
            </div>
        </div>
    );
}
